import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FaRedo } from 'react-icons/fa';
import { useMutation } from '@apollo/client';
import { promiseModalDialog, toastSuccess } from 'motion-components';
import { useTranslation } from 'react-i18next';
import { useHandleError } from 'shared-pages/services';
import { gqlResendNotification } from '../graphql';

/** Επαναποστολή σε αποτυχημένους / χωρίς email (αν έχει προστεθεί email στο σημείο) */
export const ResendNotificationButton = ({
  notificationId,
  disabled,
  noTargets,
  onResent,
}: {
  notificationId: number;
  disabled?: boolean;
  noTargets?: boolean;
  onResent: () => void;
}) => {
  const { t } = useTranslation();
  const { handleGraphqlError } = useHandleError('ResendNotification');
  const [resend, { loading }] = useMutation(gqlResendNotification);

  const handleResend = async () => {
    const confirmed = await promiseModalDialog({
      title: t('Επαναποστολή ενημέρωσης'),
      text: t(
        'Θα σταλεί ξανά μόνο σε όσους απέτυχαν ή δεν είχαν email. Όσοι έχουν ήδη απαντήσει δεν επηρεάζονται. Θέλετε να συνεχίσετε;',
      ),
    });
    if (!confirmed) return;

    try {
      const { data } = await resend({ variables: { id: notificationId } });
      const result = data?.resendNotification;
      if (!result?.id) throw new Error(t('Αποτυχία επαναποστολής'));
      const extra =
        result.skippedNoEmail > 0
          ? t(' (χωρίς email ακόμα: {{n}})', { n: result.skippedNoEmail })
          : '';
      toastSuccess(
        t('Μπήκαν σε ουρά {{queued}} παραλήπτες', { queued: result.queuedCount }) + extra,
      );
      onResent();
    } catch (error) {
      handleGraphqlError('mutation-resend', error);
    }
  };

  return (
    <Button
      onClick={handleResend}
      disabled={loading || disabled}
      className="button-animate-scale ms-2"
      type="button"
      size="sm"
      variant="outline-success"
      title={
        disabled
          ? noTargets
            ? t('Δεν υπάρχουν αποτυχημένοι ή χωρίς email για επαναποστολή')
            : t('Αποθηκεύστε πρώτα τις αλλαγές')
          : undefined
      }
    >
      {loading ? (
        <Spinner animation="border" size="sm" className="mb-1" />
      ) : (
        <span className="d-flex align-items-center">
          <FaRedo className="me-2 button-icon" />
          {t('Επαναποστολή')}
        </span>
      )}
    </Button>
  );
};
